
import React, { useState, useEffect } from 'react';
import CTASection from '@/components/CTASection';
import PageSEO from '@/components/seo/PageSEO';
import { ArrowRight, Calendar, User, Clock, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { blogPosts } from '@/data/blogPosts';

const Blog = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTopic, setSelectedTopic] = useState<string | null>(null);
  const [visiblePosts, setVisiblePosts] = useState(9); // For lazy loading
  
  // Get all unique topics from blog posts
  const allTopics = ['B2B Leadgeneratie', 'Sales Outsourcing', 'CRM Optimalisatie', 'Telefonische Prospectie', 
    'Afspraken Maken', 'Warme Leads', 'Conversie Verbeteren', 'Sales Automatisering', 
    'KMO Groei', 'Vlaamse Markt', 'Cold Calling', 'Thuiswerken', 'Contract Engine'];
  
  // Helper function to convert Dutch date to sortable format (DD month YYYY -> Date object)
  const parseDutchDate = (dateStr: string): Date => {
    const months: {[key: string]: number} = {
      'januari': 0, 'februari': 1, 'maart': 2, 'april': 3, 'mei': 4, 'juni': 5,
      'juli': 6, 'augustus': 7, 'september': 8, 'oktober': 9, 'november': 10, 'december': 11
    };
    
    const parts = dateStr.split(' ');
    if (parts.length === 3) {
      const day = parseInt(parts[0]);
      const month = months[parts[1].toLowerCase()];
      const year = parseInt(parts[2]);
      
      if (!isNaN(day) && month !== undefined && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }
    return new Date(0); // Default date if parsing fails
  };
  
  // Sort blog posts by date (newest first)
  const sortedBlogPosts = [...blogPosts].sort((a, b) => {
    const dateA = parseDutchDate(a.date);
    const dateB = parseDutchDate(b.date);
    return dateB.getTime() - dateA.getTime();
  });
  
  // Filter blog posts based on search query and selected topic
  const filteredPosts = sortedBlogPosts.filter(post => {
    const matchesSearch = searchQuery === '' || 
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesTopic = selectedTopic === null || 
      (post.tags && post.tags.includes(selectedTopic));
    
    return matchesSearch && (selectedTopic ? matchesTopic : true);
  });

  // Posts that are currently visible (for lazy loading)
  const currentlyVisiblePosts = filteredPosts.slice(0, visiblePosts);

  const handleTopicClick = (topic: string) => {
    setSelectedTopic(topic === selectedTopic ? null : topic);
    setVisiblePosts(9); // Reset visible posts when changing topics
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const clearFilters = () => {
    setSelectedTopic(null);
    setSearchQuery('');
  };

  const loadMorePosts = () => {
    setVisiblePosts(prev => prev + 6);
  };

  // Track when user scrolls to implement scroll restoration
  useEffect(() => {
    // Save scroll position when user leaves page
    const saveScrollPosition = () => {
      sessionStorage.setItem('blog-scroll-position', window.scrollY.toString());
    };

    // Restore scroll position when returning to the page
    const storedScrollPosition = sessionStorage.getItem('blog-scroll-position');
    if (storedScrollPosition) {
      window.scrollTo(0, parseInt(storedScrollPosition));
      sessionStorage.removeItem('blog-scroll-position');
    }

    window.addEventListener('beforeunload', saveScrollPosition);
    return () => {
      window.removeEventListener('beforeunload', saveScrollPosition);
    };
  }, []);

  // Create structured data for blog list
  const blogListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": currentlyVisiblePosts.map((post, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "BlogPosting",
        "headline": post.title,
        "description": post.excerpt,
        "image": post.image,
        "author": {
          "@type": "Person",
          "name": post.author
        },
        "datePublished": parseDutchDate(post.date).toISOString(),
        "publisher": {
          "@type": "Organization",
          "name": "Afsprakenmaker.be",
          "logo": {
            "@type": "ImageObject",
            "url": "https://afsprakenmaker.be/lovable-uploads/43aed143-9c8c-4ba6-8e3d-43007bdbe014.png"
          }
        },
        "mainEntityOfPage": `https://afsprakenmaker.be/blog/${post.id}`,
        "url": `https://afsprakenmaker.be/blog/${post.id}`
      }
    }))
  };
  
  return (
    <div className="min-h-screen flex flex-col">
      {/* Enhanced SEO */}
      <PageSEO
        title="B2B Sales & Leadgeneratie Blog | Afsprakenmaker.be"
        description="Praktische tips, inzichten en succesverhalen voor groeiende Vlaamse KMO's. Ontdek hoe u uw B2B sales kunt optimaliseren en meer kwalitatieve leads kunt genereren."
        keywords="B2B sales, leadgeneratie, telemarketing, Vlaamse KMO, verkoopstrategieën, afspraken maken, CRM optimalisatie"
        canonicalUrl="/blog"
        structuredData={blogListStructuredData}
      />
      
      {/* Hero Section - Increased padding top to create more space from navbar */}
      <section className="bg-brand-lightBlue pt-32 pb-16 md:pt-36 md:pb-24">
        <div className="container">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              B2B Sales & Leadgeneratie Blog
            </h1>
            <p className="text-xl text-gray-700 mb-8">
              Praktische tips, inzichten en succesverhalen voor groeiende Vlaamse KMO's.
            </p>
            <div className="bg-white p-4 rounded-lg shadow-md flex items-center">
              <input 
                type="search" 
                placeholder="Zoek in onze kennisbank..."
                className="flex-1 border-0 focus:ring-0 focus:outline-none text-gray-700"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label="Zoek artikelen"
              />
              <button 
                className="bg-brand-blue text-white p-2 rounded-md"
                aria-label="Zoeken"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </button>
            </div>
            
            {/* Active filters display */}
            {(selectedTopic || searchQuery) && (
              <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                <span className="text-sm text-gray-600">Actieve filters:</span>
                
                {selectedTopic && (
                  <span className="bg-brand-blue text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    {selectedTopic}
                    <button 
                      onClick={() => setSelectedTopic(null)} 
                      className="ml-1 hover:text-white/80"
                      aria-label={`Verwijder filter ${selectedTopic}`}
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                
                {searchQuery && (
                  <span className="bg-brand-blue text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    Zoekterm: {searchQuery}
                    <button 
                      onClick={() => setSearchQuery('')} 
                      className="ml-1 hover:text-white/80"
                      aria-label="Verwijder zoekterm"
                    >
                      <X size={14} />
                    </button>
                  </span>
                )}
                
                <button 
                  onClick={clearFilters}
                  className="text-sm text-brand-blue hover:underline"
                  aria-label="Wis alle filters"
                >
                  Wis alle filters
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Featured Post - Only show if no filters are applied */}
      {!selectedTopic && searchQuery === '' && (
        <section className="section">
          <div className="container">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-brand-lightBlue text-brand-blue px-4 py-2 rounded-full mb-4">
                  <span className="font-medium">Uitgelicht artikel</span>
                </div>
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                  <Link to={`/blog/${blogPosts[0].id}`} className="hover:text-brand-blue transition-colors">
                    {blogPosts[0].title}
                  </Link>
                </h2>
                
                <div className="flex items-center gap-6 text-sm text-gray-600 mb-6">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    <span>{blogPosts[0].date}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" />
                    <span>{blogPosts[0].author}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    <span>{blogPosts[0].readTime}</span>
                  </div>
                </div>
                
                <p className="text-gray-700 mb-6">
                  {blogPosts[0].excerpt}
                </p>
                
                <h3 className="text-xl font-bold mb-3 text-gray-800">Telemarketing evolueert met technologie</h3>
                <p className="text-gray-700 mb-4">
                  De wereld van telemarketing verandert voortdurend. Met de integratie van slimme technologie zoals Contract Engine zorgt Afsprakenmaker ervoor dat telemarketing professionals effectiever en efficiënter kunnen werken. In dit artikel duiken we dieper in hoe automatisering en AI de toekomst van het vak vormgeven...
                </p>
                
                <Link to={`/blog/${blogPosts[0].id}`} className="inline-flex items-center gap-2 text-brand-blue hover:underline font-medium">
                  Lees het volledige artikel
                  <ArrowRight size={18} />
                </Link>
                
                {blogPosts[0].source === 'bloovi' && (
                  <div className="mt-4 text-sm text-gray-500">
                    <span>Bron: </span>
                    <a href={blogPosts[0].sourceUrl} target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">
                      Bloovi
                    </a>
                  </div>
                )}
              </div>
              <div>
                <Link to={`/blog/${blogPosts[0].id}`}>
                  <img 
                    src={blogPosts[0].image}
                    alt={`Featured blog post - ${blogPosts[0].title}`}
                    className="rounded-lg shadow-md w-full h-auto hover:opacity-90 transition-opacity"
                    loading="eager" // Prioritize loading the featured image
                  />
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid - Enhanced with lazy loading */}
      <section className="bg-brand-gray section">
        <div className="container">
          <h2 className="section-title mb-6">
            {selectedTopic ? `Artikelen over ${selectedTopic}` : 'Recente artikelen'}
          </h2>
          
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-medium text-gray-700 mb-4">Geen artikelen gevonden</h3>
              <p className="text-gray-600 mb-6">
                Er zijn geen artikelen die overeenkomen met je zoekcriteria.
              </p>
              <button 
                onClick={clearFilters}
                className="bg-brand-blue text-white px-6 py-3 rounded-md hover:bg-brand-blue/90 transition-colors"
              >
                Alle filters wissen
              </button>
            </div>
          ) : (
            <>
              <div className="grid md:grid-cols-3 gap-8">
                {currentlyVisiblePosts.map((post, index) => (
                  <div key={post.id} className="bg-white rounded-lg overflow-hidden shadow-md" itemScope itemType="https://schema.org/BlogPosting">
                    <Link to={`/blog/${post.id}`} className="block h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title} 
                        className="w-full h-full object-cover transition-transform hover:scale-105"
                        loading={index < 6 ? "eager" : "lazy"} // Lazy load images below the fold
                        itemProp="image"
                      />
                    </Link>
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-xs text-gray-500 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          <span itemProp="datePublished">{post.date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>
                      
                      <h3 className="text-xl font-semibold mb-3 text-gray-900" itemProp="headline">
                        <Link to={`/blog/${post.id}`} className="hover:text-brand-blue transition-colors">
                          {post.title}
                        </Link>
                      </h3>
                      
                      <p className="text-gray-600 mb-4" itemProp="description">
                        {post.excerpt}
                      </p>

                      {/* Add topic tags for SEO and UX */}
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 3).map((tag, index) => (
                            <button
                              key={index}
                              onClick={() => handleTopicClick(tag)}
                              className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs hover:bg-brand-lightBlue hover:text-brand-blue transition-colors"
                            >
                              {tag}
                            </button>
                          ))}
                        </div>
                      )}
                      
                      <div className="flex justify-between items-center">
                        <Link to={`/blog/${post.id}`} className="inline-flex items-center gap-1 text-brand-blue hover:underline text-sm font-medium">
                          Lees meer
                          <ArrowRight size={14} />
                        </Link>
                        
                        {post.source === 'bloovi' && (
                          <div className="text-xs text-gray-500">
                            <a href={post.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-brand-blue hover:underline">
                              Bron: Bloovi
                            </a>
                          </div>
                        )}
                      </div>

                      {/* Hidden structured data elements */}
                      <meta itemProp="author" content={post.author} />
                      <link itemProp="mainEntityOfPage" href={`https://afsprakenmaker.be/blog/${post.id}`} />
                    </div>
                  </div>
                ))}
              </div>

              {/* Load more button - only show if there are more posts to load */}
              {visiblePosts < filteredPosts.length && (
                <div className="text-center mt-8">
                  <button
                    onClick={loadMorePosts}
                    className="bg-white border border-brand-blue text-brand-blue px-6 py-3 rounded-md hover:bg-brand-lightBlue transition-colors"
                  >
                    Laad meer artikelen
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="section">
        <div className="container">
          <div className="bg-brand-lightBlue rounded-lg p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4 text-gray-900">
                  Ontvang onze nieuwste inzichten
                </h2>
                <p className="text-gray-700 mb-0">
                  Schrijf je in voor onze nieuwsbrief en ontvang elke maand de beste tips en strategieën voor B2B sales en leadgeneratie, speciaal voor Vlaamse KMO's.
                </p>
              </div>
              <div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input 
                    type="email" 
                    placeholder="Jouw e-mailadres" 
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-brand-blue"
                    aria-label="E-mailadres voor nieuwsbrief"
                  />
                  <button className="btn-primary whitespace-nowrap">
                    Schrijf me in
                  </button>
                </div>
                <p className="text-xs text-gray-600 mt-2">
                  Door je in te schrijven ga je akkoord met onze privacyverklaring. Je kan je op elk moment uitschrijven.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Topics Section - Enhanced with topic clustering */}
      <section className="section bg-white">
        <div className="container">
          <h2 className="section-title mb-8">Populaire onderwerpen</h2>
          
          <div className="flex flex-wrap gap-4 justify-center">
            {allTopics.map((topic, index) => (
              <button 
                key={index} 
                onClick={() => handleTopicClick(topic)}
                className={`px-4 py-2 rounded-full transition-colors ${
                  selectedTopic === topic 
                    ? 'bg-brand-blue text-white' 
                    : 'bg-brand-lightBlue text-brand-blue hover:bg-brand-blue hover:text-white'
                }`}
              >
                {topic}
              </button>
            ))}
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <CTASection />
    </div>
  );
};

export default Blog;
